import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { INavItem, MenuItemModel } from './models/nav-item.model';
import { APPLICATION_SELECTORS } from '../../core/store/selectors/application.selector';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { startWith, takeUntil, tap } from 'rxjs/operators';
import { selectUrl } from '../../core/store/selectors/router.selector';
import { Actions, ofType } from '@ngrx/effects';
import { HeaderService } from '../header';
import { Router } from '@angular/router';
import { ROOT_ROUTES } from 'src/app/core/configurations/router.config';
import { Sidebar, SidebarModule } from 'primeng/sidebar';
import { IStarIconActionPayload, PanelMenuComponent } from './components/panel-menu/panel-menu.component';
import { USER_PREFERENCES_ACTIONS } from '../../core/store/actions/user-preferences.actions';
import { USER_PREFERENCES_REDUCER } from '../../core/store/reducers/user-preferences.reducer';
import { NAVIGATION_SIDEBAR_BASE_Z_INDEX } from '../../core/configurations/layout.config';
import { cloneDeep } from 'lodash';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ShowAfterPipe } from './pipes/show-after.pipe';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FilterNavMenuPipe } from './pipes/filter-nav-menu.pipe';
import { AsyncPipe, NgClass } from '@angular/common';
import { NavigationItemComponent } from './components/navigation-item/navigation-item.component';
import { IsNavItemActivePipe } from './pipes/navigation.item.pipe';
import { InputTextModule } from 'primeng/inputtext';
import { ROUTER_REQUEST } from '@ngrx/router-store';
import { RouterNavigatedAction } from '@ngrx/router-store/src/actions';
import { DividerModule } from 'primeng/divider';
import { FilterFavouriteSubmenuPipe } from './pipes/filter-favourite-submenu.pipe';

@Component({
  selector: 'lh-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    IconFieldModule,
    InputIconModule,
    ShowAfterPipe,
    FormsModule,
    ButtonModule,
    PanelMenuComponent,
    FilterNavMenuPipe,
    SidebarModule,
    NgClass,
    NavigationItemComponent,
    AsyncPipe,
    IsNavItemActivePipe,
    InputTextModule,
    DividerModule,
    FilterFavouriteSubmenuPipe,
  ],
})
export class NavigationComponent implements OnInit, OnDestroy {
  @ViewChild('sidebar') readonly sidebar?: Sidebar;
  selectedNavItem: INavItem | null = null;
  searchString: string = '';
  navItems: INavItem[] = [];

  private readonly isNavigationOpened$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly NAVIGATION_SIDEBAR_BASE_Z_INDEX = NAVIGATION_SIDEBAR_BASE_Z_INDEX;
  readonly data$: Observable<INavigationComponentData> = combineLatest({
    isHeaderHidden: this.headerService.headerHidden$,
    currentUrl: this.store.select(selectUrl),
    isDesktop: this.store.select(APPLICATION_SELECTORS.selectIsDesktop),
    isNavigationPinned: this.store.select(USER_PREFERENCES_REDUCER.isNavigationPinned),
    isNavigationOpened: this.isNavigationOpened$,
    favouriteSubmenu: this.store.select(APPLICATION_SELECTORS.selectFavouriteSubmenu),
  });

  private readonly ROOT_ROUTE_REGEX = /^\/([^\/]*).*$/;
  private readonly onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    readonly headerService: HeaderService,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly window: Window,
  ) {}

  ngOnInit(): void {
    this.initNavigationListener();
    void this.initNavItems();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onStarIconClick({ event, menuItem: { routerLink } }: IStarIconActionPayload): void {
    event.preventDefault();
    event.stopPropagation();

    if (!routerLink) return;

    this.store.dispatch(USER_PREFERENCES_ACTIONS.toggleFavouritePage({ pageUrl: routerLink }));
  }

  selectNavItem(navItem: INavItem): void {
    const isNavItemToggled = this.selectedNavItem?.url === navItem.url;

    if (navItem.standalone) {
      this.handleStandAloneNavItem(navItem);
    } else if (isNavItemToggled) {
      void this.closeNavMenu();
    } else {
      this.showNavMenu(navItem);
    }
  }

  showNavMenu(navItem: INavItem): void {
    this.searchString = '';
    this.selectedNavItem = navItem;
    this.isNavigationOpened$.next(true);
  }

  navigateToNewItem(rootRoute: ROOT_ROUTES | string, buttonUrl: string): void {
    void this.router.navigate([`./${rootRoute}/${buttonUrl}`]);
  }

  pinNavigation(): void {
    if (this.sidebar?.visible) this.sidebar?.disableModality();
    this.store.dispatch(USER_PREFERENCES_ACTIONS.pinNavigation());
  }

  unpinNavigation(): void {
    if (this.sidebar?.visible) this.sidebar?.enableModality();
    this.store.dispatch(USER_PREFERENCES_ACTIONS.unpinNavigation());
  }

  onVisibilityChange(isVisible: boolean): void {
    if (!isVisible) void this.closeNavMenu();
  }

  async closeNavMenu(unpin: boolean = false): Promise<void> {
    const { isNavigationPinned, isDesktop } = await firstValueFrom(this.data$);

    if (!isDesktop || !isNavigationPinned || unpin) {
      this.selectedNavItem = null;
      this.isNavigationOpened$.next(false);
    }

    if (isNavigationPinned && unpin) {
      this.unpinNavigation();
    }
  }

  private initNavigationListener(): void {
    this.actions$
      .pipe(
        takeUntil(this.onDestroy$),
        ofType(ROUTER_REQUEST),
        startWith(null),
        tap(async (value: null | RouterNavigatedAction): Promise<void> => {
          const url = value ? value.payload.event.url : (await firstValueFrom(this.data$)).currentUrl;
          void this.onNavigationChanged(url);
        }),
      )
      .subscribe();
  }

  private handleStandAloneNavItem(navItem: INavItem): void {
    const isExternalLink: boolean = navItem.url.includes('http');

    if (isExternalLink) {
      this.window.open(navItem.url);
    } else {
      void this.router.navigate([`./${navItem.url}`]);
    }
  }

  private async onNavigationChanged(url: string): Promise<void> {
    const navItems = await firstValueFrom(APPLICATION_SELECTORS.selectNavigationConfig(this.store));
    const { isNavigationPinned, isDesktop } = await firstValueFrom(this.data$);

    if (isNavigationPinned && isDesktop) {
      const activeRootRoute = url.replace(this.ROOT_ROUTE_REGEX, '$1');
      const navItem = navItems.find((item) => item.url === activeRootRoute);
      if (navItem && !navItem.standalone) {
        void this.showNavMenu(navItem);
      } else {
        void this.closeNavMenu(true);
      }
    } else {
      void this.closeNavMenu();
    }
  }

  /**
   * make a deep copy of the navigation configuration array for allowing p-panelMenu mutate it
   * (for example clicking on "expand arrow" p-panelMenu mutate and save the state in the original array,
   * that is why it is important to copy array from store to give a control of managing state of the navigation to p-panelMenu)
   */
  private async initNavItems(): Promise<void> {
    const navItems = await firstValueFrom(APPLICATION_SELECTORS.selectNavigationConfig(this.store));
    this.navItems = cloneDeep(navItems); // make a deep copy to allow side nav mutate the model
    this.cdRef.detectChanges();
  }
}

interface INavigationComponentData {
  currentUrl: string;
  isNavigationPinned: boolean;
  isHeaderHidden: boolean;
  isNavigationOpened: boolean;
  isDesktop: boolean;
  favouriteSubmenu: MenuItemModel | null;
}
